import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CheckCircle, Crown, CreditCard, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (v) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(Number(v || 0));
const seatLimit = (key) => key === "pro" ? 5 : key === "business" ? 25 : 1;

export default function Billing() {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [iframeUrl, setIframeUrl] = useState("");
  const [loading, setLoading] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [contact, setContact] = useState({ user_name: "", user_address: "", user_phone: "" });
  const [params] = useSearchParams();

  const load = async () => {
    const [packages, history] = await Promise.all([
      api.get("/billing/packages"),
      api.get("/billing/orders").catch(() => ({ data: [] })),
    ]);
    setData(packages.data);
    setOrders(history.data || []);
    try {
      const [me, settings] = await Promise.all([api.get("/auth/me"), api.get("/settings")]);
      setContact({
        user_name: me.data?.name || "",
        user_address: settings.data?.company_address || "",
        user_phone: settings.data?.company_phone || "",
      });
    } catch (_) {}
  };

  useEffect(() => { load().catch(() => {}); }, []);
  useEffect(() => {
    if (!iframeUrl) return;
    const id = "paytr-resizer";
    const activate = () => window.iFrameResize?.({}, "#paytriframe");
    const existing = document.getElementById(id);
    if (existing) { activate(); return; }
    const script = document.createElement("script");
    script.id = id;
    script.src = "https://www.paytr.com/js/iframeResizer.min.js";
    script.async = true;
    script.onload = activate;
    document.body.appendChild(script);
  }, [iframeUrl]);

  const current = data?.current;
  const packages = useMemo(() => data?.packages || {}, [data]);

  const checkout = async (plan) => {
    if (!accepted) { toast.error("Devam etmek için sözleşme ve politikaları kabul edin."); return; }
    setLoading(plan);
    try {
      const r = await api.post("/billing/paytr/token", { plan, ...contact });
      setIframeUrl(r.data.iframe_url);
      setTimeout(() => document.getElementById("paytr-checkout")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(""); }
  };

  return (
    <div>
      <PageHeader title="Abonelik & Ödeme" subtitle="Paketinizi ve PayTR ödeme geçmişinizi yönetin" />
      <div className="p-4 sm:p-8 space-y-6 max-w-6xl">
        {params.get("paytr") === "success" && (
          <div className="border border-blue-200 bg-blue-50 p-4 flex gap-3 text-sm">
            <CheckCircle size={22} className="text-blue-600 shrink-0" />
            <div><b>Ödeme sayfası tamamlandı.</b><br/>Aboneliğiniz yalnız PayTR sunucu bildirimi doğrulandıktan sonra aktif edilir. Birkaç saniye içinde bu ekranı yenileyebilirsiniz.</div>
          </div>
        )}
        {params.get("paytr") === "failed" && (
          <div className="border border-red-200 bg-red-50 p-4 flex gap-3 text-sm">
            <WarningCircle size={22} className="text-red-600 shrink-0" />
            <div><b>Ödeme tamamlanamadı.</b> Tekrar deneyebilir veya ödeme geçmişindeki durumu kontrol edebilirsiniz.</div>
          </div>
        )}

        <section className="bg-white border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Mevcut Paket</div>
          <div className="mt-1 flex flex-wrap items-end gap-3">
            <div className="text-3xl font-bold">{current?.label || "—"}</div>
            {current?.expires_at && <div className="text-sm text-slate-500 mb-1">Bitiş: {new Date(current.expires_at).toLocaleString("tr-TR")}</div>}
          </div>
          {current?.limits && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
              {Object.entries(current.limits).map(([k, limit]) => (
                <div key={k} className="bg-slate-50 border border-slate-100 p-3">
                  <div className="text-[10px] uppercase text-slate-500">{k}</div>
                  <b>{current.used?.[k] ?? 0} / {limit == null ? "∞" : limit}</b>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="grid md:grid-cols-2 gap-4">
          {["pro", "business"].map((key) => {
            const p = packages[key]; if (!p) return null;
            return (
              <section key={key} className={`bg-white border p-5 ${key === "business" ? "border-slate-900" : "border-blue-200"}`}>
                <div className="flex items-center justify-between">
                  <div><div className="text-xs uppercase text-slate-500">30 Gün</div><h2 className="text-xl font-bold">{p.label}</h2></div>
                  <Crown size={24} weight="duotone" />
                </div>
                <div className="text-3xl font-extrabold mt-4">{money(p.monthly_price_try)}</div>
                <div className="space-y-2 mt-4 text-sm">
                  <div className="flex gap-2"><CheckCircle size={17}/> <b>{seatLimit(key)} kullanıcı</b> · Ekip & Yetkiler</div>
                  <div className="flex gap-2"><CheckCircle size={17}/> Aylık plan kotası: {p.plan_limit ?? "Sınırsız"}</div>
                  <div className="flex gap-2"><CheckCircle size={17}/> Ürün kotası: {p.product_limit ?? "Sınırsız"}</div>
                  <div className="flex gap-2"><CheckCircle size={17}/> Şablon kotası: {p.template_limit ?? "Sınırsız"}</div>
                </div>
                <button onClick={() => checkout(key)} disabled={!!loading || !p.monthly_price_try}
                  className="mt-5 w-full bg-[#0F172A] text-white py-3 font-semibold disabled:opacity-50 inline-flex justify-center items-center gap-2">
                  <CreditCard size={18}/>{loading === key ? "PayTR hazırlanıyor…" : "PayTR ile Öde"}
                </button>
              </section>
            );
          })}
        </div>

        <section className="bg-white border border-slate-200 p-5 space-y-3">
          <h3 className="font-bold">Fatura / ödeme bilgileri</h3>
          <div className="grid md:grid-cols-3 gap-3">
            <input value={contact.user_name} onChange={(e) => setContact({...contact, user_name:e.target.value})} className="border p-2.5" placeholder="Ad / Firma" />
            <input value={contact.user_phone} onChange={(e) => setContact({...contact, user_phone:e.target.value})} className="border p-2.5" placeholder="Telefon" />
            <input value={contact.user_address} onChange={(e) => setContact({...contact, user_address:e.target.value})} className="border p-2.5" placeholder="Adres" />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" />
            <span><Link className="text-blue-600 underline" to="/terms" target="_blank">Kullanım Şartları</Link>, <Link className="text-blue-600 underline" to="/privacy" target="_blank">Gizlilik Politikası</Link> ve <Link className="text-blue-600 underline" to="/cancellation-refund" target="_blank">İptal/İade Politikası</Link>'nı okudum ve kabul ediyorum.</span>
          </label>
        </section>

        {iframeUrl && (
          <section id="paytr-checkout" className="bg-white border border-slate-200 p-3 sm:p-5">
            <h3 className="font-bold mb-3">Güvenli PayTR Ödeme</h3>
            <iframe src={iframeUrl} id="paytriframe" title="PayTR Ödeme" frameBorder="0" scrolling="no" style={{ width:"100%", minHeight:720 }} />
          </section>
        )}

        <section className="bg-white border border-slate-200 overflow-x-auto">
          <div className="p-4 border-b font-bold">Ödeme Geçmişi</div>
          <table className="data-table w-full min-w-[700px]"><thead><tr><th>Sipariş</th><th>Paket</th><th>Tutar</th><th>Durum</th><th>Tarih</th></tr></thead>
            <tbody>{orders.map((o) => <tr key={o.merchant_oid}><td className="font-mono text-xs">{o.merchant_oid}</td><td>{o.package}</td><td>{money(o.amount_try)}</td><td>{o.status}</td><td>{o.created_at ? new Date(o.created_at).toLocaleString("tr-TR") : "—"}</td></tr>)}
            {!orders.length && <tr><td colSpan={5} className="text-center text-slate-400 py-6">Henüz ödeme kaydı yok.</td></tr>}</tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
